// ResetPassword.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../constants/api";
import { showError, promise, handleError } from "../../utils/toast.js";
import { Loader2, ShieldCheck, AlertCircle, Eye, EyeOff, Mail, Lock } from "lucide-react";
import AppButton from "../ui/AppButton.jsx";
import { successButtonLoadingClassName } from "../../design/tokens.js";
import { cn } from "../../utils/themeClasses.js";
import { Input } from "../forms/shared/FormAtoms.jsx";
import {
  AuthPageLayout,
  AuthScreenHeadline,
  authLoginInputClassName,
  authLabelClassName,
} from "./AuthVisualShell.jsx";

const MIN_LEN = Number(import.meta?.env?.VITE_PASS_MIN_LEN || 8);
const REQUIRE_SPECIAL = String(import.meta?.env?.VITE_PASS_REQUIRE_SPECIAL || "0") === "1";
const RULES = {
  minLen: MIN_LEN,
  classesRequired: 3,
  classes: [
    { id: "lower", test: /[a-z]/, label: "Kleinbuchstabe" },
    { id: "upper", test: /[A-Z]/, label: "Grossbuchstabe" },
    { id: "digit", test: /\d/, label: "Zahl" },
    { id: "special", test: /[^A-Za-z0-9]/, label: "Sonderzeichen" },
  ],
  requireSpecial: REQUIRE_SPECIAL,
};
function evaluatePassword(pw) {
  const p = String(pw || "");
  const lenOk = p.length >= RULES.minLen;
  const hits = RULES.classes.filter(c => c.test.test(p)).length;
  const baseOk = hits >= RULES.classesRequired;
  const specialOk = !RULES.requireSpecial || /[^A-Za-z0-9]/.test(p);
  const classesOk = baseOk && specialOk;

  let score = 0;
  if (lenOk) score++;
  if (hits >= 2) score++;
  if (hits >= 3) score++;
  if (p.length >= RULES.minLen + 4 && hits === 4) score++;
  return {
    lenOk,
    classesOk,
    hits,
    score,
    valid: lenOk && classesOk,
  };
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("init");
  const [identifier, setIdentifier] = useState("");
  const [token, setToken] = useState("");
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t =
      params.get("token") ||
      (window.location.hash.startsWith("#token=") ? window.location.hash.slice(7) : "");
    if (t) {
      setStage("complete");
      setToken(t);
    }
  }, []);
  const pwEval = useMemo(() => evaluatePassword(p1), [p1]);
  const canInit = identifier.trim().length >= 3 && !loading;
  const canComplete = !loading && p1.length > 0 && p2.length > 0 && p1 === p2 && pwEval.valid && !!token;
  const init = async e => {
    e.preventDefault();
    setMsg("");
    setErr("");
    setLoading(true);
    try {
      await promise(
        api.post("/password/reset/init", {
          identifier: identifier.trim(),
        }),
        {
          loading: "Sende Link…",
          success: "ok.saved",
          error: "srv.error",
        },
      );
      setMsg("Wenn ein Konto existiert, wurde ein Reset-Link per E-Mail versendet.");
    } catch {
      setMsg("Wenn ein Konto existiert, wurde ein Reset-Link per E-Mail versendet.");
    } finally {
      setLoading(false);
    }
  };
  const complete = async e => {
    e.preventDefault();
    setMsg("");
    setErr("");
    if (p1 !== p2) return setErr("Passwörter stimmen nicht überein.");
    if (!pwEval.valid) return setErr("Das neue Passwort erfüllt die Anforderungen nicht.");
    setLoading(true);
    try {
      const { data } = await promise(
        api.post("/password/reset/complete", {
          token,
          newPassword: p1,
        }),
        {
          loading: "Speichere…",
          success: "Passwort gesetzt.",
          error: "srv.error",
        },
      );
      if (data?.erfolg) {
        setStage("done");
        setMsg("Passwort gesetzt. Du kannst dich jetzt einloggen.");
      } else {
        const m = data?.msg || "Zurücksetzen fehlgeschlagen.";
        if (/ungültig|invalid/i.test(m)) setErr("Der Link ist ungültig. Fordere bitte einen neuen an.");
        else if (/ablauf|expired/i.test(m)) setErr("Der Link ist abgelaufen. Fordere bitte einen neuen an.");
        else setErr(m);
        showError(m);
      }
    } catch (e) {
      const m = e?.response?.data?.msg || "Zurücksetzen fehlgeschlagen.";
      if (e?.response?.status === 410) setErr("Der Link ist abgelaufen. Fordere bitte einen neuen an.");
      else if (e?.response?.status === 400) setErr("Der Link ist ungültig. Fordere bitte einen neuen an.");
      else setErr(m);
      handleError(e, "srv.error");
    } finally {
      setLoading(false);
    }
  };

  const fieldPw = `${authLoginInputClassName} !pr-12`;

  return (
    <AuthPageLayout docTitle="Passwort zurücksetzen • WireTrack">
      {(msg || err) && (
        <div
          className={`rounded-2xl px-4 py-3 text-sm flex items-start gap-2 border ${
            err ? "bg-red-500/10 text-red-100 border-red-500/25" : "bg-green-500/10 text-green-100 border-green-500/25"
          }`}
          role="status"
        >
          {err ? <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> : <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />}
          <p>{err || msg}</p>
        </div>
      )}

      {stage === "init" && (
        <>
          <AuthScreenHeadline variant="hero" title="Passwort zurücksetzen" />
        <form onSubmit={init} className="space-y-3 sm:space-y-[1.125rem]" noValidate>
          <div>
            <label htmlFor="identifier" className={authLabelClassName}>
              E-Mail oder Benutzername
            </label>
            <div className="relative group">
              <span
                className="pointer-events-none absolute left-3.5 top-1/2 flex h-[22px] w-[22px] -translate-y-1/2 items-center justify-center"
                aria-hidden
              >
                <Mail className="login-field-icon login-field-icon--mail h-[18px] w-[18px] shrink-0 text-white/25 transition-colors group-focus-within:text-blue-500" />
              </span>
              <Input
                id="identifier"
                type="text"
                className={authLoginInputClassName}
                placeholder="name@firma.ch oder Benutzername"
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                autoComplete="username"
                inputMode="email"
                aria-required="true"
              />
            </div>
          </div>

          <AppButton
            type="submit"
            variant="primary"
            disabled={!canInit}
            className={cn(
              "login-submit-premium relative overflow-hidden w-full justify-center gap-2.5 font-semibold tracking-tight max-sm:min-h-[50px] max-sm:text-[15px] min-h-[52px] text-[15px] sm:min-h-[54px] sm:text-base rounded-[1rem] disabled:!opacity-100 wrtk-btn",
              !canInit ? "cursor-not-allowed" : "",
            )}
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin shrink-0" /> : null}
            {loading ? "Sende Link…" : "Link anfordern"}
          </AppButton>

          <p className="text-xs leading-relaxed text-white/40">
            Wenn die Adresse existiert, senden wir einen Link. Aus Sicherheitsgründen zeigen wir keine Treffer an.
          </p>
        </form>
        </>
      )}

      {stage === "complete" && (
        <>
          <AuthScreenHeadline
            variant="hero"
            title="Neues Passwort wählen"
            description="Wähle ein starkes Passwort und bestätige es einmal."
          />
        <form onSubmit={complete} className="space-y-3 sm:space-y-[1.125rem]" noValidate>
          <PwField id="new-password" label="Neues Passwort" fieldClass={fieldPw} value={p1} onChange={setP1} show={showPw} setShow={setShowPw} autoComplete="new-password" />
          <PwField id="new-password-repeat" label="Wiederholen" fieldClass={fieldPw} value={p2} onChange={setP2} show={showPw} setShow={setShowPw} autoComplete="new-password" />

          <StrengthMeter evalRes={pwEval} sample={p1} />

          {p2 && p1 !== p2 && <p className="text-xs font-medium text-red-300">Passwörter stimmen nicht überein.</p>}

          <AppButton
            type="submit"
            variant="primary"
            disabled={!canComplete}
            className={cn(
              "login-submit-premium relative overflow-hidden w-full justify-center gap-2.5 font-semibold tracking-tight max-sm:min-h-[50px] max-sm:text-[15px] min-h-[52px] text-[15px] sm:min-h-[54px] sm:text-base rounded-[1rem] disabled:!opacity-100 wrtk-btn",
              !canComplete ? "cursor-not-allowed" : "",
              loading && successButtonLoadingClassName,
            )}
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin shrink-0" /> : null}
            {loading ? "Speichere…" : "Speichern"}
          </AppButton>

          <p className="text-xs text-white/40">
            Der Link ist zeitlich begrenzt. Bei „ungültig“ oder „abgelaufen“ bitte neuen Link anfordern.
          </p>

          <div className="pt-1">
            <AppButton
              type="button"
              variant="ghost"
              onClick={() => setStage("init")}
              className="text-xs font-normal text-sky-300/90 hover:text-sky-100 h-auto min-h-[44px] !p-0 !border-0 !shadow-none hover:underline underline-offset-2"
            >
              Anderen Link anfordern
            </AppButton>
          </div>
        </form>
        </>
      )}

      {stage === "done" && (
        <div className="space-y-5">
          <AuthScreenHeadline
            variant="hero"
            title="Alles bereit"
            description="Du kannst dich jetzt mit dem neuen Passwort anmelden."
          />
          <div className="rounded-2xl p-4 text-sm bg-green-500/10 border border-green-500/25 text-green-100">
            Passwort gesetzt. Du kannst dich jetzt einloggen.
          </div>
          <AppButton
            type="button"
            variant="primary"
            onClick={() => navigate("/login")}
            className="login-submit-premium relative overflow-hidden w-full justify-center gap-2.5 font-semibold tracking-tight max-sm:min-h-[50px] max-sm:text-[15px] min-h-[52px] text-[15px] sm:min-h-[54px] sm:text-base rounded-[1rem] wrtk-btn"
          >
            Zum Login
          </AppButton>
        </div>
      )}
    </AuthPageLayout>
  );
}

function PwField({ id, label, value, onChange, show, setShow, autoComplete, fieldClass }) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className={authLabelClassName}>
        {label}
      </label>
      <div className="relative group">
        <span
          className="pointer-events-none absolute left-3.5 top-1/2 flex h-[22px] w-[22px] -translate-y-1/2 items-center justify-center"
          aria-hidden
        >
          <Lock className="login-field-icon login-field-icon--lock h-[18px] w-[18px] shrink-0 text-white/25 transition-colors group-focus-within:text-blue-500" />
        </span>
        <Input
          id={id}
          type={show ? "text" : "password"}
          className={fieldClass}
          placeholder={label}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoComplete={autoComplete}
          aria-required="true"
        />
        <AppButton
          type="button"
          variant="ghost"
          onClick={() => setShow(s => !s)}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-lg min-h-0 h-auto w-auto border-0 shadow-none !text-white/30 hover:!text-white/70 hover:!bg-white/5"
          aria-label={show ? "Passwort verbergen" : "Passwort anzeigen"}
          title={show ? "Passwort verbergen" : "Passwort anzeigen"}
          tabIndex={-1}
        >
          {show ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
        </AppButton>
      </div>
    </div>
  );
}

function StrengthMeter({ evalRes, sample = "" }) {
  const labels = ["Sehr schwach", "Schwach", "Okay", "Gut", "Sehr gut"];
  const pct = (evalRes.score / 4) * 100;
  return (
    <div className="space-y-2">
      <div className="h-2 rounded overflow-hidden bg-white/10">
        <div
          className="h-full transition-all"
          style={{
            width: `${pct}%`,
            background:
              evalRes.score >= 4
                ? "linear-gradient(90deg, #34d399, #10b981)"
                : evalRes.score >= 3
                  ? "linear-gradient(90deg, #60a5fa, #3b82f6)"
                  : evalRes.score >= 2
                    ? "linear-gradient(90deg, #f59e0b, #fbbf24)"
                    : "linear-gradient(90deg, #ef4444, #f87171)",
          }}
          aria-hidden
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs text-blue-200/70">
        <span className="font-medium">Stärke: {labels[evalRes.score]}</span>
        <span>
          {evalRes.lenOk ? `≥${RULES.minLen} Zeichen` : `≥${RULES.minLen} Zeichen fehlt`} · {evalRes.hits}/4 Klassen
          {RULES.requireSpecial ? " · Sonderzeichen nötig" : ""}
        </span>
      </div>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-blue-200/60">
        {RULES.classes.map(c => {
          const ok = c.test.test(sample);
          return (
            <li key={c.id} className="flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-green-400" : "bg-white/20"}`} aria-hidden />
              {c.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
